import { Component, computed, inject, TemplateRef, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { UserService } from './services/user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../environments/environment';
import { HealthService } from './services/health.service';
import { combineLatest, filter, firstValueFrom } from 'rxjs';
import { WorkspaceService } from './services/workspace.service';
import { AppService, BrandInfo } from './services/app.service';
import { SafeHtml } from '@angular/platform-browser';
import { BrandLoadingComponent } from "./components/general/brand-loading/brand-loading.component";
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FooterComponent } from "./components/general/footer/footer.component";
import { UserInterface } from 'voxr-shared';
import { SoftphoneComponent } from './components/integration/softphone/softphone.component';
import { SoftphoneService } from './services/softphone.service';
import { SoftphoneExceptionService } from './services/softphone-exception.service';
import { SocketService } from './services/socket.service';

@Component({
	selector: 'app-root',
	standalone: true,
	imports: [
		RouterOutlet, CommonModule,
		MatTooltipModule, MatIconModule, MatButtonModule,
		BrandLoadingComponent,
		FooterComponent,
		SoftphoneComponent,
	],
	providers: [],
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
})
export class AppComponent {
	private router = inject(Router);
	private app = inject(AppService);
	private userService = inject(UserService);
	private workspaceService = inject(WorkspaceService);
	private healthService = inject(HealthService);
	private snackBar = inject(MatSnackBar);
	private exceptions = inject(SoftphoneExceptionService);
	private socketService = inject(SocketService);

	loading = true;
	deployment = environment.deployment;
	serverId?: Object;
	brand?: BrandInfo;
	user?: UserInterface;
	brandCss = computed(() => this.app.brandCss());
	icon!: string;

	@ViewChild('messageTemplate') messageTemplate!: TemplateRef<any>;
	messageRef?: MatSnackBarRef<any>;

	constructor() {
		this.exceptions.start();
		this.userService.user$.pipe(takeUntilDestroyed()).subscribe(user => this.exceptions.setUser(user?._id?.toString()));
		inject(SoftphoneService);
		combineLatest([this.userService.user$, this.app.brand$]).pipe(
			takeUntilDestroyed(),
			filter(([user, brand]) => brand !== undefined),
		).subscribe(async ([user, brand]) => await this.load(user, brand));
		this.app.newVersion$.pipe(takeUntilDestroyed(), filter(() => !this.app.skipNewVersion)).subscribe(() => this.newVersion());
		this.app.message$.pipe(takeUntilDestroyed()).subscribe((message) => {
			const displayedMessage = {
				...message,
				severity: message.severity || 'info' as const,
				duration: message.duration === -1 ? undefined : message.duration || 3000,
			};
			this.app.recordDisplayedToast(displayedMessage);
			this.messageRef = this.snackBar.openFromTemplate(this.messageTemplate, {
				data: displayedMessage,
				duration: displayedMessage.duration,
				panelClass: `severity-${displayedMessage.severity}`,
			});
		});
		this.checkQueries();
	}

	async ngOnInit() {
		this.serverId = await this.healthService.serverId();
	}

	newVersion() {
		const snackbarRef = this.snackBar.open('A new version of the app is available. Refresh to update.', 'Refresh');
		snackbarRef.onAction().subscribe(() => location.reload());
	}

	skipBranding = false;
	private async load(user?: UserInterface, brand?: BrandInfo) {
		this.user = user;
		this.skipBranding = this.app.skipBranding;
		if (user) await firstValueFrom(this.workspaceService.workspaces$);
		document.querySelector('body')?.classList.add('uw-ready');
		setTimeout(() => this.loading = false, !brand?._cached ? 2000 : 0);
	}

	private checkQueries() {
		try {
			if (location.search.includes('iframe')) {
				const path = localStorage.getItem('iframe-pathname');
				if (path) {
					const urlTree = this.router.parseUrl(path);
					delete urlTree.queryParams['iframe'];
					this.router.navigateByUrl(urlTree, { replaceUrl: true });
				}
				this.router.events.pipe(filter(e => e instanceof NavigationEnd))
					.subscribe(e => localStorage.setItem('iframe-pathname', e.url));
			}
			if (location.search.includes('impersonate=')) {
				localStorage.setItem('impersonate', location.search.split('impersonate=')[1].split('&')[0]);
				this.socketService.refreshAuthenticationContext();
				if (this.app.idToken) void this.userService.current();
				const urlTree = this.router.parseUrl(location.pathname);
				delete urlTree.queryParams['impersonate'];
				this.router.navigateByUrl(urlTree, { replaceUrl: true });
			}
		} catch (e) {
			console.error(e);
		}
	}


}
